// ABOUTME: Actions component for top posts in the Insights page
// ABOUTME: Provides View Post, Share Link, Copy Link, and Duplicate actions

import { useCallback, useState } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  CopyPlusIcon,
  DropdownMenu,
  EllipsisVerticalIcon,
  ExternalLinkIcon,
  IconButton,
  LinkIcon,
  ForwardIcon,
  toast,
  Tooltip,
  ZapIcon,
} from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import {
  EXPIRED_STORY_LINK_MESSAGE,
  isExpiredStory,
} from '~publish/helpers/isExpiredStory'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { isRpcUpdate } from '~publish/legacy/post/helpers'
import { useFetchPostForComposer } from '~publish/hooks/postForComposer/useFetchPostForComposer'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import styles from './TopPostActions.module.css'

interface TopPostActionsProps {
  postId: string
  channelId: string
  externalLink: string | null | undefined
  allowedActions: string[]
  /** When false, hides the composer-opening menu items (share, duplicate). */
  canCreatePosts?: boolean
  boostUrl?: string | null
  /** `metadata.type`, paired with `sentAt` to spot an expired Story. */
  postType?: string | null
  sentAt?: string | null
  ctas?: {
    shareLink?: CTA
    duplicate?: CTA
  }
}

/** A link action the user cannot take, with the reason on hover. */
function ExpiredStoryItem({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Tooltip content={EXPIRED_STORY_LINK_MESSAGE} side="right" sideOffset={4}>
      <DropdownMenu.Item disabled>{children}</DropdownMenu.Item>
    </Tooltip>
  )
}

export function TopPostActions({
  postId,
  channelId,
  externalLink,
  allowedActions,
  canCreatePosts = true,
  boostUrl,
  postType,
  sentAt,
  ctas = {},
}: TopPostActionsProps): JSX.Element | null {
  const organizationId = useOrganizationId()
  const {
    triggerAttributes,
    createNewPostInComposer,
    duplicatePostInComposer,
  } = usePostComposer()
  const fetchPostForComposer = useFetchPostForComposer()
  const [duplicateLoading, setDuplicateLoading] = useState(false)

  const handleBoostClick = useCallback(() => {
    BufferTracker.cTAClicked({
      clientName: 'publishWeb',
      cta: 'publish-insights-topPosts-boostLinkedIn-1',
      upgradePathName: null,
      organizationId: organizationId ?? undefined,
      product: 'publish',
    })
  }, [organizationId])

  const isExpired = isExpiredStory({ postType, sentAt })
  const canViewPost = externalLink && allowedActions.includes('viewPost')
  const canCopyLink = externalLink && allowedActions.includes('copyPostLink')
  // Sharing opens the composer, so posting access is checked here — the
  // backend grants `sharePostLink` to view-only actors (it only gates on the
  // post being sent with an external link).
  const canShareLink =
    canCreatePosts && externalLink && allowedActions.includes('sharePostLink')
  const canDuplicate = allowedActions.includes('duplicatePost')
  // Hidden, not disabled — the disabled explainer below is about non-Buffer
  // posts, not permissions.
  const showDuplicate = canCreatePosts

  const handleCopyLink = useCallback(async () => {
    if (!externalLink) return

    try {
      await navigator.clipboard.writeText(externalLink)
      toast.success('Link copied to the clipboard')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast.error(`Failed to copy link: ${message}`)
    }
  }, [externalLink])

  const handleShareLink = useCallback(async () => {
    if (!externalLink) return

    await createNewPostInComposer({
      cta: ctas.shareLink,
      prefillPostData: { text: externalLink },
    })
  }, [externalLink, createNewPostInComposer, ctas.shareLink])

  const handleDuplicate = useCallback(async () => {
    setDuplicateLoading(true)

    try {
      const post = await fetchPostForComposer(postId)
      if (!isRpcUpdate(post)) {
        toast.error('Failed to load post for duplication')
        return
      }

      // Omit fields that shouldn't be duplicated
      const {
        id: _id,
        due_at: _dueAt,
        day: _day,
        scheduled_at: _scheduledAt,
        scheduledAt: _scheduledAtCamel,
        serviceUpdateId: _serviceUpdateId,
        createdAt: _createdAt,
        statistics: _statistics,
        status: _status,
        serviceLink: _serviceLink,
        tags,
        ...rest
      } = post

      void duplicatePostInComposer({
        cta: ctas.duplicate,
        channels: [channelId],
        prefillPostData: {
          ...rest,
          tags: tags?.map((tag: { id: string }) => tag.id),
          isSent: false,
          isPastDue: false,
          isDraft: false,
        },
        duplicatedFrom: postId,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast.error(`Failed to duplicate post: ${message}`)
    } finally {
      setDuplicateLoading(false)
    }
  }, [
    postId,
    channelId,
    duplicatePostInComposer,
    fetchPostForComposer,
    ctas.duplicate,
  ])

  const hasMenuItems = Boolean(
    canViewPost || boostUrl || canShareLink || canCopyLink || showDuplicate,
  )
  if (!hasMenuItems) {
    return null
  }

  return (
    <div className={styles.wrapper}>
      <DropdownMenu modal={false}>
        <DropdownMenu.Trigger asChild>
          <IconButton
            data-testid="more-actions-menu"
            label="More actions"
            tooltip="More actions"
            variant="tertiary"
          >
            <EllipsisVerticalIcon />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          <DropdownMenu.Group>
            {canViewPost &&
              (isExpired ? (
                <ExpiredStoryItem>
                  <ExternalLinkIcon />
                  View Post
                </ExpiredStoryItem>
              ) : (
                <DropdownMenu.Item asChild>
                  <a
                    href={externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLinkIcon />
                    View Post
                  </a>
                </DropdownMenu.Item>
              ))}

            {boostUrl && (
              <DropdownMenu.Item asChild>
                <a
                  href={boostUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleBoostClick}
                >
                  <ZapIcon />
                  Boost on LinkedIn
                </a>
              </DropdownMenu.Item>
            )}

            {canShareLink &&
              (isExpired ? (
                <ExpiredStoryItem>
                  <ForwardIcon />
                  Share Link in a Post
                </ExpiredStoryItem>
              ) : (
                <DropdownMenu.Item
                  {...triggerAttributes}
                  onSelect={handleShareLink}
                >
                  <ForwardIcon />
                  Share Link in a Post
                </DropdownMenu.Item>
              ))}

            {canCopyLink &&
              (isExpired ? (
                <ExpiredStoryItem>
                  <LinkIcon />
                  Copy link
                </ExpiredStoryItem>
              ) : (
                <DropdownMenu.Item onSelect={handleCopyLink}>
                  <LinkIcon />
                  Copy link
                </DropdownMenu.Item>
              ))}

            {showDuplicate &&
              (canDuplicate ? (
                <DropdownMenu.Item
                  {...triggerAttributes}
                  loading={duplicateLoading}
                  onSelect={handleDuplicate}
                >
                  <CopyPlusIcon />
                  Duplicate
                </DropdownMenu.Item>
              ) : (
                <Tooltip
                  content="We can only duplicate posts shared via Buffer"
                  side="right"
                  sideOffset={4}
                >
                  <DropdownMenu.Item disabled>
                    <CopyPlusIcon />
                    Duplicate
                  </DropdownMenu.Item>
                </Tooltip>
              ))}
          </DropdownMenu.Group>
        </DropdownMenu.Content>
      </DropdownMenu>
    </div>
  )
}
